"use client"

import { useState, useTransition } from "react"
import { Modal, TextInput, Select, Button, Stack, Text, Alert, Group, TbCheck, TbAlertCircle } from "@forestsync/ui"
import { useTranslations } from "next-intl"
import { createTradingProfile } from "@/actions/profile"
import { getAllCountries } from "@forestsync/db"

interface TradingProfileModalProps {
  opened: boolean
  onClose: () => void
  onSuccess: () => void
}

export function TradingProfileModal({ opened, onClose, onSuccess }: TradingProfileModalProps) {
  const t = useTranslations("trading.tradingProfileModal")
  const [isPending, startTransition] = useTransition()
  const [name, setName] = useState("")
  const [country, setCountry] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const countryOptions = getAllCountries().map((c) => ({ value: c.code, label: c.name }))

  function handleSubmit() {
    setError(null)
    if (!name.trim()) { setError(t("nameLabel") + " is required"); return }
    if (!country) { setError(t("countryLabel") + " is required"); return }

    startTransition(async () => {
      const result = await createTradingProfile({ name: name.trim(), country })
      if (!result.success) {
        setError(result.error ?? "Failed to create trading profile")
        return
      }
      setSuccess(true)
    })
  }

  function handleClose() {
    if (success) onSuccess()
    else onClose()
    setName("")
    setCountry(null)
    setError(null)
    setSuccess(false)
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t("title")}
      centered
      size="sm"
    >
      {success ? (
        <Stack gap="md" align="center" py="md">
          <TbCheck size={48} color="var(--mantine-color-green-6)" />
          <Stack gap="xs" align="center">
            <Text fw={600}>{t("successTitle")}</Text>
            <Text c="dimmed" ta="center" size="sm">{t("successDescription")}</Text>
          </Stack>
          <Button onClick={handleClose} fullWidth>
            {t("submit")}
          </Button>
        </Stack>
      ) : (
        <Stack gap="md">
          <Text c="dimmed" size="sm">{t("description")}</Text>

          {error && (
            <Alert icon={<TbAlertCircle size={16} />} color="red" variant="light">
              {error}
            </Alert>
          )}

          <TextInput
            label={t("nameLabel")}
            placeholder={t("namePlaceholder")}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            required
          />

          <Select
            label={t("countryLabel")}
            placeholder={t("countryPlaceholder")}
            data={countryOptions}
            value={country}
            onChange={setCountry}
            searchable
            required
          />

          <Group justify="flex-end" mt="xs">
            <Button variant="subtle" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={isPending}>
              {isPending ? t("submitting") : t("submit")}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  )
}
